import { useEffect, useRef, useState } from "react";
import { TextField } from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useDragOrClick, useDragParentGroup } from "./useDragParentGroup";

export default function SchemeSceneContent({
  parentGroupId,
  hasScreenshot,
  imageKey,
  imageSrc,
  buttonCount = 0,
  maxButtons = 2,
  onVideoSelected,
  onAddButton,
  buttonText,
  onButtonTextChange,
}) {
  const [panel, setPanel] = useState(null);
  const [imgError, setImgError] = useState(false);
  const fileInputRef = useRef(null);

  const startGroupDrag = useDragParentGroup(parentGroupId);
  const openVideoPanel = useDragOrClick(parentGroupId, () => setPanel("video"));
  const openButtonPanel = useDragOrClick(parentGroupId, () => setPanel("button"));

  const canAddButton = buttonCount < maxButtons;
  const showScreenshot = hasScreenshot && imageSrc && !imgError;

  useEffect(() => {
    setImgError(false);
  }, [imageSrc, imageKey]);

  const closePanel = () => setPanel(null);

  const handleVideoPick = (event) => {
    onVideoSelected(event);
    closePanel();
  };

  const handleAddButton = () => {
    onAddButton();
    closePanel();
  };

  const mediaClassName = [
    "scheme-scene__media-wrap",
    "nodrag",
    "nopan",
    showScreenshot
      ? "scheme-scene__media-wrap--filled"
      : imgError
        ? "scheme-scene__media-wrap--error"
        : "scheme-scene__media-wrap--empty",
  ].join(" ");

  const handleCardPointerDown = (event) => {
    if (
      event.target.closest(
        ".scheme-scene__media-wrap, .scheme-scene__tool, .scheme-scene__overlay"
      )
    ) {
      return;
    }
    startGroupDrag(event);
  };

  return (
    <div
      className="scheme-scene__card"
      onPointerDown={handleCardPointerDown}
    >
      <div
        className={mediaClassName}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {showScreenshot && (
          <img
            key={imageKey}
            src={imageSrc}
            alt=""
            className="scheme-scene__thumb"
            onError={() => setImgError(true)}
          />
        )}

        {panel && (
          <div
            className="scheme-scene__overlay nodrag nopan"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="scheme-scene__overlay-close"
              onClick={closePanel}
              aria-label="Закрыть"
            >
              <CloseIcon fontSize="small" />
            </button>

            {panel === "button" ? (
              <div className="scheme-scene__overlay-body">
                <span className="scheme-scene__overlay-title">Новая кнопка</span>
                <TextField
                  placeholder="Текст кнопки"
                  variant="outlined"
                  size="small"
                  fullWidth
                  autoFocus
                  value={buttonText}
                  onChange={(event) => onButtonTextChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleAddButton();
                  }}
                />
                <button
                  type="button"
                  className="scheme-scene__overlay-submit"
                  onClick={handleAddButton}
                >
                  Добавить
                </button>
              </div>
            ) : (
              <div className="scheme-scene__overlay-body">
                <span className="scheme-scene__overlay-title">Загрузка видео</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={handleVideoPick}
                />
                <button
                  type="button"
                  className="scheme-scene__overlay-submit"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Выбрать файл
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {!panel && (
        <div
          className={`scheme-scene__toolbar${
            !canAddButton ? " scheme-scene__toolbar--single" : ""
          }`}
        >
          <button
            type="button"
            className="scheme-scene__tool"
            onPointerDown={openVideoPanel}
          >
            <VideocamOutlinedIcon fontSize="inherit" />
            {hasScreenshot ? "Заменить" : "Видео"}
          </button>

          {canAddButton && (
            <button
              type="button"
              className="scheme-scene__tool"
              onPointerDown={openButtonPanel}
            >
              <AddIcon fontSize="inherit" />
              Кнопка
            </button>
          )}
        </div>
      )}
    </div>
  );
}
